// src/components/fiscal/VoidNumberModal.js
import React, { useState, useEffect } from "react";
import { X, ShieldAlert, Loader2, Send, CheckCircle } from "lucide-react";
import { fiscalService } from "../../services/fiscalService";

export default function VoidNumberModal({ isOpen, onClose, configs }) {
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [serie, setSerie] = useState("1");
  const [numeroInicial, setNumeroInicial] = useState("");
  const [numeroFinal, setNumeroFinal] = useState("");
  const [ano, setAno] = useState(() => String(new Date().getFullYear()).slice(-2));
  const [justificativa, setJustificativa] = useState("");

  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [successResult, setSuccessResult] = useState(null);

  // --- SELECIONAR CONFIG PADRÃO ---
  useEffect(() => {
    if (configs && configs.length > 0) {
      setSelectedConfigId(configs[0].id);
    }
  }, [configs, isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setSuccessResult(null);
    setValidationErrors([]);
    setNumeroInicial("");
    setNumeroFinal("");
    setJustificativa("");
    onClose();
  };

  const handleVoid = async (e) => {
    e.preventDefault();
    setValidationErrors([]);
    setSuccessResult(null);

    const config = configs.find((c) => c.id === selectedConfigId);
    if (!config) {
      setValidationErrors(["Selecione uma empresa emitente válida."]);
      return;
    }

    const errors = [];
    const numIni = parseInt(numeroInicial, 10);
    const numFim = parseInt(numeroFinal, 10);

    if (isNaN(numIni) || numIni <= 0) {
      errors.push("O número inicial deve ser um número inteiro maior que 0.");
    }
    if (isNaN(numFim) || numFim <= 0) {
      errors.push("O número final deve ser um número inteiro maior que 0.");
    }
    if (!isNaN(numIni) && !isNaN(numFim) && numIni > numFim) {
      errors.push("O número inicial não pode ser maior que o número final.");
    }
    if (!serie.trim()) {
      errors.push("A série da numeração é obrigatória.");
    }
    if (!ano.trim() || ano.length !== 2) {
      errors.push("O ano deve conter exatamente 2 dígitos (ex: 26 para 2026).");
    }
    if (justificativa.trim().length < 15) {
      errors.push("A justificativa deve conter no mínimo 15 caracteres.");
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const token = (config.ambiente === "producao" ? config.token_producao : config.token_homologacao || "").trim();
      const payload = {
        cnpj: config.cnpj.replace(/\D/g, ""),
        ano: ano.trim(),
        serie: serie.trim(),
        numero_inicial: String(numIni),
        numero_final: String(numFim),
        justificativa: justificativa.trim(),
        ambiente: config.ambiente,
        token: token,
      };

      const result = await fiscalService.voidNumberRange(payload);
      console.log("Response void n8n:", result);

      if (result && (result.status === "homologado" || result.status === "autorizado" || result.cStat === "102" || result.cStat === 102 || result.protocolo)) {
        setSuccessResult({
          protocolo: result.protocolo || result.nProt || "Inutilização Homologada",
          mensagem: result.mensagem || result.xMotivo || "Inutilização de número homologada com sucesso pela SEFAZ.",
        });
      } else {
        const errMsg = result.mensagem || result.xMotivo || (result.erros && result.erros[0]?.mensagem) || "Rejeição desconhecida pela SEFAZ.";
        throw new Error(errMsg);
      }
    } catch (err) {
      console.error("Erro ao inutilizar faixa:", err);
      setValidationErrors([err.message || "Erro de conexão com o servidor."]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-550 text-white" style={{ backgroundColor: "#1e293b" }}>
          <div className="flex items-center gap-2">
            <ShieldAlert size={20} className="text-amber-400" />
            <h2 className="font-bold text-sm uppercase tracking-wide">Inutilizar Numeração (SEFAZ)</h2>
          </div>
          <button onClick={handleClose} className="p-1 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleVoid} className="flex-1 overflow-y-auto p-6 space-y-4">
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-xs space-y-1">
              <strong className="font-bold block">Atenção:</strong>
              <ul className="list-disc pl-4 space-y-0.5">
                {validationErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {successResult ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-xl text-center space-y-3 animate-scaleUp">
              <CheckCircle size={44} className="text-emerald-500 mx-auto" />
              <h3 className="font-bold text-sm uppercase">Inutilização Concluída!</h3>
              <p className="text-xs text-slate-600">{successResult.mensagem}</p>
              <div className="bg-white/80 border border-emerald-100 rounded-lg p-3 inline-block font-mono text-[11px] font-bold text-slate-800">
                Protocolo: {successResult.protocolo}
              </div>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-emerald-100 cursor-pointer"
                >
                  Fechar Janela
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Empresa Emitente */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                  Empresa Emitente
                </label>
                <div className="grid grid-cols-1 gap-2.5">
                  {configs.map((conf) => (
                    <label
                      key={conf.id}
                      className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                        selectedConfigId === conf.id
                          ? "border-blue-500 bg-blue-50/50 shadow-sm"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="void_config_cnpj"
                        value={conf.id}
                        checked={selectedConfigId === conf.id}
                        onChange={() => setSelectedConfigId(conf.id)}
                        className="text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-700 truncate">{conf.razao_social}</p>
                        <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                          CNPJ: {conf.cnpj.replace(/\D/g, "").replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")} | {conf.ambiente.toUpperCase()}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Serie, Ano, Numero Inicial, Numero Final */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Série</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-lg text-xs font-mono font-medium focus:ring-1 focus:ring-blue-500"
                    value={serie}
                    onChange={(e) => setSerie(e.target.value)}
                    placeholder="1"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Ano (2 Dígitos)</label>
                  <input
                    type="text"
                    maxLength={2}
                    className="w-full p-2 border rounded-lg text-xs font-mono font-medium focus:ring-1 focus:ring-blue-500 text-center"
                    value={ano}
                    onChange={(e) => setAno(e.target.value.replace(/\D/g, ""))}
                    placeholder="26"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Nº Inicial</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-lg text-xs font-mono font-medium focus:ring-1 focus:ring-blue-500 text-right"
                    value={numeroInicial}
                    onChange={(e) => setNumeroInicial(e.target.value)}
                    placeholder="120"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"></div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Nº Final</label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-lg text-xs font-mono font-medium focus:ring-1 focus:ring-blue-500 text-right"
                    value={numeroFinal}
                    onChange={(e) => setNumeroFinal(e.target.value)}
                    placeholder="120"
                    required
                  />
                </div>
              </div>

              {/* Justificativa */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">
                  Justificativa (Mínimo 15 caracteres)
                </label>
                <textarea
                  className="w-full p-2.5 border rounded-lg text-xs font-medium focus:ring-1 focus:ring-blue-500"
                  rows={3}
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  placeholder="Justificativa da quebra de sequência numérica (ex: Falha de impressão ou erro técnico na numeração 120)."
                  required
                />
                <span className="text-[10px] text-slate-400 block text-right">
                  {justificativa.length} / 15 caracteres mínimos
                </span>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-4 py-2 border border-slate-200 text-slate-500 hover:bg-slate-50 rounded-lg text-xs font-bold transition-all cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-1.5 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-all shadow-md shadow-red-100 cursor-pointer disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <Send size={13} />
                      <span>Confirmar Inutilização</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
